import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    InputAdornment,
    Typography,
    Button,
    FormControlLabel,
    Switch
} from "@mui/material";
import InfoButton from "../../Tooltip/InfoTextField";
import { PaymentProviderConfigViewModel } from "../../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../Context/Contexts";

type Props = {
    viewModel: PaymentProviderConfigViewModel | null;
    onSave: (vm: PaymentProviderConfigViewModel) => void;
};

const KlarnaPaymentProviderWizard: React.FC<Props> = ({ viewModel, onSave }) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const [localViewModel, setLocalViewModel] = useState<PaymentProviderConfigViewModel>(viewModel ?? {});

    const handleChange = (field: keyof PaymentProviderConfigViewModel, value: string | null) => {
        setLocalViewModel((prev) => ({ ...prev, [field]: value }));
    };

    const handleFinish = () => {
        if (!localViewModel.description || localViewModel.description.trim() === "") {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return;
        }
        onSave(localViewModel);
    };

    const cancelWizard = () => {
        window.history.back();
    };

    const handleKlarnaProviderTypeChange = (type: "kco" | "kp") => {
        setLocalViewModel((prev) => ({
            ...prev,
            paymentProviderInfo5: prev.paymentProviderInfo5 === type ? "" : type,
        }));
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", px: 2 }}>
            <Typography variant="h6" gutterBottom>
                Klarna
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={t("common.description")}
                        value={localViewModel.description || ""}
                        onChange={(e) => handleChange("description", e.target.value)}
                        fullWidth
                        margin="normal"
                        error={!localViewModel.description || localViewModel.description.trim() === ""}
                        helperText={
                            !localViewModel.description?.trim() ? t("help.descriptionRequired") : ""
                        }
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.klarnaInfo1")}
                        value={localViewModel.paymentProviderInfo1 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo1", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.klarnaMerchantId")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.klarnaInfo2")}
                        value={localViewModel.paymentProviderInfo2 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo2", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.klarnaSharedSecret")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.klarnaInfo3")}
                        value={localViewModel.paymentProviderInfo3 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo3", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.klarnaBaseUrl")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <FormControlLabel
                    control={
                        <Switch
                            checked={localViewModel.paymentProviderInfo5 === "kco"}
                            onChange={() => handleKlarnaProviderTypeChange("kco")}
                            color="primary"
                        />
                    }
                    label="Klarna Checkout (KCO)"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={localViewModel.paymentProviderInfo5 === "kp"}
                            onChange={() => handleKlarnaProviderTypeChange("kp")}
                            color="primary"
                        />
                    }
                    label="Klarna Payments (KP)"
                />

                <Grid item xs={12}>
                    <TextField
                        label={t("common.klarnaInfo6")}
                        value={localViewModel.paymentProviderInfo6 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo6", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.klarnaTermsUrl")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.klarnaInfo7")}
                        value={localViewModel.paymentProviderInfo7 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo7", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.klarnaConfirmationUrl")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.klarnaInfo8")}
                        value={localViewModel.paymentProviderInfo8 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo8", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.klarnaCheckoutUrl1")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.klarnaInfo9")}
                        value={localViewModel.paymentProviderInfo9 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo9", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.klarnaCheckoutUrl2")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
            </Grid>

            <Box sx={{ marginTop: 2, textAlign: "center" }}>
                <Button variant="contained" color="primary" onClick={handleFinish} sx={{ mr: 2 }}>
                    {t("common.finish")}
                </Button>
                <Button variant="contained" color="warning" onClick={cancelWizard}>
                    {t("common.cancelWizard")}
                </Button>
            </Box>
        </Box>
    );
};

export default KlarnaPaymentProviderWizard;
